import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Chip
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { storage } from '../../firebase';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';

function Photos() {
  const navigate = useNavigate();
  
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Reference to the user images folder
        const storageRef = ref(storage, 'bsi-bass-userimages');
        
        // Recursively fetch all images
        const allPhotos = await fetchAllImagesRecursively(storageRef);
        
        // Sort by creation time (most recent first)
        allPhotos.sort((a, b) => b.timeCreated - a.timeCreated);
        
        // Take the first 50 most recent images
        const recentPhotos = allPhotos.slice(0, 50);
        
        setPhotos(recentPhotos);
      } catch (err) {
        console.error('Error fetching photos:', err);
        setError('Failed to load photos from storage');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // Recursive function to fetch all images from nested folders
  const fetchAllImagesRecursively = async (folderRef) => {
    const allImages = [];
    
    try {
      const result = await listAll(folderRef);
      
      // Process all files in current folder
      for (const itemRef of result.items) {
        console.log(`allImages: ${allImages.length}`);
        if (allImages.length > 0) {
            return allImages;
        }

        try {
          // Get metadata to check if it's an image and get creation time
          const metadata = await getMetadata(itemRef);
          const contentType = metadata.contentType || '';
          
          if (contentType.startsWith('image/') && itemRef.fullPath.toLowerCase().endsWith('_thumbnail.jpg')) {
            console.log(`file: ${itemRef.fullPath} with content type: ${contentType}`);
            const thumbnailUrl = await getDownloadURL(itemRef);
            
            // Get the full-size image URL by removing _thumbnail from the path
            const fullPath = itemRef.fullPath.replace(/_thumbnail\.jpg$/i, '.jpg');
            const fullImageRef = ref(storage, fullPath);
            let fullImageUrl = thumbnailUrl; // Fallback to thumbnail if full image doesn't exist
            
            try {
              fullImageUrl = await getDownloadURL(fullImageRef);
            } catch (err) {
              console.warn(`Full image not found for ${fullPath}, using thumbnail`);
            }
            
            allImages.push({
              name: itemRef.name,
              fullPath: itemRef.fullPath,
              thumbnailUrl: thumbnailUrl,
              fullImageUrl: fullImageUrl,
              contentType: contentType,
              timeCreated: new Date(metadata.timeCreated),
              size: metadata.size
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch image ${itemRef.fullPath}:`, err);
        }
      }
      
      // Recursively process all subfolders
      for (const prefixRef of result.prefixes) {
        const subfolderImages = await fetchAllImagesRecursively(prefixRef);
        allImages.push(...subfolderImages);

        if (allImages.length >= 50) {
            return allImages; // Return early if limit reached
        }
      }
    } catch (err) {
      console.error(`Error listing folder ${folderRef.fullPath}:`, err);
    }
    
    return allImages;
  };

  const handleImageClick = (photo) => {
    setSelectedPhoto(photo);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPhoto(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Photos
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Showing {photos.length} most recent images from user storage
      </Typography>

      {photos.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No photos found in storage
          </Typography>
        </Paper>
      ) : (
        <ImageList
          sx={{ width: '100%', height: 'auto' }}
          cols={4}
          gap={16}
          rowHeight={250}
        >
          {photos.map((photo) => (
            <ImageListItem key={photo.fullPath}>
              <img
                src={photo.thumbnailUrl}
                alt={photo.name}
                loading="lazy"
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%'
                }}
                onClick={() => handleImageClick(photo)}
              />
              <ImageListItemBar
                title={photo.name}
                subtitle={formatDate(photo.timeCreated)}
                actionIcon={
                  <IconButton
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                    onClick={() => handleImageClick(photo)}
                  >
                    <ZoomInIcon />
                  </IconButton>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Image Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 2
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedPhoto?.name}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPhoto && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200
                }}
              >
                <img
                  src={selectedPhoto.fullImageUrl}
                  alt={selectedPhoto.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Path:</strong> {selectedPhoto.fullPath}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Uploaded:</strong> {formatDate(selectedPhoto.timeCreated)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Size:</strong> {formatFileSize(selectedPhoto.size)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Type:</strong> <Chip label={selectedPhoto.contentType} size="small" />
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Photos;
